import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getAccess } from '../services/authService';

const plans = [
  {
    id: 'free',
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    desc: 'Explore Pulse with no commitment.',
    gradA: '#334155', gradB: '#1e293b',
    features: [
      { text: '500 messages / month' },
      { text: '1 website training / month' },
      { text: '1 document training / month' },
      { text: 'Web & WhatsApp Support' },
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    monthlyPrice: 999,
    yearlyPrice: 799,
    desc: 'For small teams ready to automate customer support.',
    gradA: '#479EC3', gradB: '#3B88A9',
    features: [
      { text: '5,000 messages / month', bonus: '+2,000 free' },
      { text: '2 website trainings / month' },
      { text: '3 document trainings / month' },
      { text: 'Web & WhatsApp Support' },
    ],
  },
  {
    id: 'growth',
    name: 'Growth',
    tag: null,
    highlight: true,
    monthlyPrice: 1999,
    yearlyPrice: 1599,
    desc: 'Best value for growing businesses scaling support.',
    gradA: '#479EC3', gradB: '#22D3EE',
    features: [
      { text: '12,000 messages / month', bonus: '+4,000 free' },
      { text: '3 website trainings / month' },
      { text: '5 document trainings / month' },
      { text: 'Web & WhatsApp Support' },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    monthlyPrice: 4999,
    yearlyPrice: 3999,
    desc: 'Power through peak demand with maximum capacity.',
    gradA: '#6366f1', gradB: '#479EC3',
    features: [
      { text: '30,000 messages / month', bonus: '+10,000 free' },
      { text: '10 website trainings / month' },
      { text: '10 document trainings / month' },
      { text: 'Web & WhatsApp Support' },
    ],
  },
];

const ORDER = ['free', 'starter', 'growth', 'pro'];

function fmt(n) { return n === 0 ? 'Free' : `₹${n.toLocaleString('en-IN')}`; }

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function Plans() {
  const { user, logout } = useAuth();
  const [yearly, setYearly] = useState(false);
  const [accessData, setAccessData] = useState({ emailVerified: true, items: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    getAccess()
      .then(setAccessData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const access = accessData.items;
  const pulseAccess = access.find(a => a.productId === 'pulse' && a.status === 'active');
  const currentPlanId = pulseAccess?.plan?.toLowerCase() ?? 'free';
  const currentPlanIdx = ORDER.indexOf(currentPlanId);

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 32, height: 32, border: '3px solid rgba(71,158,195,0.2)', borderTop: '3px solid #479EC3', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ background: '#0f172a', color: '#e2e8f0', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Background */}
      <div style={{ position: 'fixed', inset: 0, opacity: 0.04, backgroundImage: 'linear-gradient(to right,#22D3EE 1px,transparent 1px),linear-gradient(to bottom,#22D3EE 1px,transparent 1px)', backgroundSize: '64px 64px', pointerEvents: 'none' }} />
      <div style={{ position: 'fixed', top: 0, left: '30%', width: 600, height: 400, background: 'radial-gradient(ellipse,rgba(71,158,195,0.08) 0%,transparent 70%)', pointerEvents: 'none' }} />

      {/* Top bar */}
      <div style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 20px', height: 64, display: 'flex', alignItems: 'center', gap: 12 }}>

          {/* Back */}
          <Link to="/dashboard/pulse" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 13, fontWeight: 500, textDecoration: 'none', flexShrink: 0, transition: 'color .15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#e2e8f0'}
            onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 7H2M6 3L2 7l4 4"/></svg>
          </Link>

          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

          {/* Pulse branding */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <div style={{ width: 28, height: 28, borderRadius: 7, background: 'linear-gradient(135deg,#479EC3,#22D3EE)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
                <polyline points="4,16 8,16 10,9 13,23 16,12 19,20 22,16 28,16" stroke="#fff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>Pulse</div>
              <div style={{ fontSize: 10.5, color: '#479EC3', fontWeight: 600, letterSpacing: '0.05em' }}>by Reano AI</div>
            </div>
          </div>

          <div style={{ flex: 1 }} />

          {/* User + company */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {user?.companyName && (
                <span style={{ fontSize: 12.5, fontWeight: 700, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>
                  {user.companyName}
                </span>
              )}
              {user?.companyId && (
                <span style={{ fontSize: 11, color: '#334155', fontWeight: 400 }}>{user.companyId}</span>
              )}
            </div>
            <span style={{ fontSize: 11, color: '#475569', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 180 }}>{user?.email}</span>
          </div>

          <div style={{ width: 1, height: 18, background: 'rgba(255,255,255,0.08)', flexShrink: 0 }} />

          {/* Sign out */}
          <button onClick={logout} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#64748b', display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, transition: 'color .15s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#94a3b8'}
            onMouseLeave={e => e.currentTarget.style.color = '#64748b'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            <span className="plans-signout-label" style={{ fontSize: 13, fontWeight: 600 }}>Sign Out</span>
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 540px) {
          .plans-signout-label { display: none; }
          .plans-grid { grid-template-columns: 1fr !important; }
          .plans-main { padding: 28px 14px 60px !important; }
        }
      `}</style>

      <main className="plans-main" style={{ maxWidth: 1100, margin: '0 auto', padding: '52px 24px 80px', position: 'relative', zIndex: 1 }}>

        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} style={{ marginBottom: 44, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Billing & Plans</p>
          <h1 style={{ fontSize: 'clamp(24px,3vw,36px)', fontWeight: 800, letterSpacing: '-.03em', marginBottom: 8 }}>
            {pulseAccess
              ? <>You're on the <span style={{ background: 'linear-gradient(135deg,#479EC3,#22D3EE)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pulseAccess.plan ? pulseAccess.plan.charAt(0).toUpperCase() + pulseAccess.plan.slice(1) : 'Free'}</span> plan</>
              : 'Choose your plan'}
          </h1>
          {pulseAccess?.periodEnd && (
            <p style={{ fontSize: 14, color: '#64748b' }}>
              Renews <span style={{ color: '#94a3b8', fontWeight: 600 }}>{fmtDate(pulseAccess.periodEnd)}</span>
            </p>
          )}
        </motion.div>

        {/* Billing toggle */}
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 40 }}>
          <span style={{ fontSize: 13.5, color: yearly ? '#475569' : '#e2e8f0', fontWeight: yearly ? 400 : 600, transition: 'color .2s' }}>Monthly</span>
          <button onClick={() => setYearly(y => !y)} style={{
            width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer', position: 'relative',
            background: yearly ? 'linear-gradient(135deg,#479EC3,#22D3EE)' : 'rgba(71,85,105,0.5)',
            transition: 'background .25s', padding: 0,
          }}>
            <span style={{
              position: 'absolute', top: 3, left: yearly ? 23 : 3,
              width: 18, height: 18, borderRadius: '50%', background: '#fff',
              transition: 'left .25s', display: 'block',
            }} />
          </button>
          <span style={{ fontSize: 13.5, color: yearly ? '#e2e8f0' : '#475569', fontWeight: yearly ? 600 : 400, transition: 'color .2s' }}>
            Yearly
            <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 700, color: '#22D3EE', background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.25)', borderRadius: 5, padding: '2px 6px' }}>
              Save ~20%
            </span>
          </span>
        </motion.div>

        {/* Plans grid */}
        <div className="plans-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 18 }}>
          {plans.map((p, i) => {
            const isCurrent = p.id === currentPlanId;
            const isUpgrade = ORDER.indexOf(p.id) > currentPlanIdx;
            const price = yearly ? p.yearlyPrice : p.monthlyPrice;
            const saving = p.monthlyPrice > 0 ? (p.monthlyPrice - p.yearlyPrice) * 12 : 0;

            return (
              <motion.div key={p.id}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 + i * 0.07 }}
                style={{
                  background: isCurrent ? 'rgba(71,158,195,0.08)' : 'rgba(15,23,42,0.7)',
                  backdropFilter: 'blur(16px)',
                  border: `1.5px solid ${isCurrent ? 'rgba(71,158,195,0.4)' : p.highlight ? 'rgba(71,158,195,0.2)' : 'rgba(30,41,59,0.8)'}`,
                  borderTop: `3px solid ${isCurrent ? '#479EC3' : `${p.gradA}`}`,
                  borderRadius: 16, padding: 24,
                  display: 'flex', flexDirection: 'column',
                  position: 'relative',
                }}>

                {/* Badges */}
                <div style={{ display: 'flex', gap: 6, marginBottom: 16, height: 24, flexShrink: 0, overflow: 'hidden' }}>
                  {isCurrent && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, color: '#22D3EE', background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.3)' }}>
                      Current Plan
                    </span>
                  )}
                  {p.tag && !isCurrent && (
                    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, color: '#479EC3', background: 'rgba(71,158,195,0.1)', border: '1px solid rgba(71,158,195,0.25)' }}>
                      {p.tag}
                    </span>
                  )}
                </div>

                {/* Plan name */}
                <h2 style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-.02em', color: '#f1f5f9', marginBottom: 6 }}>{p.name}</h2>
                <p style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5, marginBottom: 18, minHeight: 36 }}>{p.desc}</p>

                {/* Price */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                    <span style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', letterSpacing: '-.03em' }}>{fmt(price)}</span>
                    {price > 0 && <span style={{ fontSize: 13, color: '#475569' }}>/mo</span>}
                  </div>
                  <p style={{ fontSize: 11.5, color: '#22D3EE', marginTop: 4, fontWeight: 500, visibility: yearly && saving > 0 ? 'visible' : 'hidden' }}>
                    Save ₹{saving.toLocaleString('en-IN')}/year
                  </p>
                </div>

                {/* Features */}
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8, flex: 1, marginBottom: 24 }}>
                  {p.features.map(f => (
                    <li key={f.text} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#94a3b8' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <span>
                        {f.text}
                        {f.bonus && (
                          <span style={{
                            marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#22D3EE',
                            background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.25)',
                            borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle', whiteSpace: 'nowrap',
                          }}>
                            <Zap size={9} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />
                            {f.bonus}
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {isCurrent ? (
                  <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 13, fontWeight: 600, color: '#479EC3', background: 'rgba(71,158,195,0.08)', border: '1px solid rgba(71,158,195,0.2)', borderRadius: 9 }}>
                    Current Plan
                  </div>
                ) : isUpgrade ? (
                  <a href={`mailto:support@reanoai.com?subject=Upgrade to Pulse ${encodeURIComponent(p.name)} Plan`}
                    style={{
                      display: 'block', textAlign: 'center', textDecoration: 'none',
                      padding: '10px 0', borderRadius: 9,
                      background: `linear-gradient(135deg,${p.gradA},${p.gradB})`,
                      color: '#fff', fontSize: 13.5, fontWeight: 700,
                      transition: 'opacity .2s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}
                  >
                    Upgrade to {p.name}
                  </a>
                ) : (
                  <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 13, color: '#334155', background: 'rgba(30,41,59,0.5)', border: '1px solid rgba(30,41,59,0.8)', borderRadius: 9 }}>
                    Contact support to downgrade
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Contact note */}
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          style={{ textAlign: 'center', marginTop: 36, fontSize: 13, color: '#334155' }}>
          Need a custom plan?{' '}
          <a href="mailto:support@reanoai.com" style={{ color: '#479EC3', textDecoration: 'none', fontWeight: 600 }}>
            Contact us
          </a>
        </motion.p>

      </main>
    </div>
  );
}
